import { User, Lock, Eye } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { LOGO_MARK_URL, AID_QR_TARGET, AID_DEMO_PHOTO } from "@/data/content";

const FEATURES = [
  { Icon: User, lines: ["Sharing", "by me"] },
  { Icon: Lock, lines: ["My data.", "My control."] },
  { Icon: Eye, lines: ["Accessible", "experiences."] },
];

// The A-ID Accessibility Identity card (self-contained; QR fully inside the frame).
export const AidCard = () => {
  const qrTarget = `${window.location.origin}${AID_QR_TARGET}`;
  return (
  <div
    className="relative overflow-hidden rounded-3xl border border-teal/30 p-5 sm:p-6 backdrop-blur"
    style={{ background: "linear-gradient(155deg, rgba(14,28,51,0.6), rgba(8,15,28,0.55))" }}
    role="img"
    aria-label="Example A-ID accessibility identity card for the fictional demo user Jordan Williams"
    data-testid="aid-identity-card"
  >
    {/* Aiga Enable logo watermark */}
    <img
      src={LOGO_MARK_URL}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute -left-6 bottom-0 w-36 opacity-[0.06] select-none sm:w-44"
    />

    {/* top: identity block (left) + QR (top-right, inside the card) */}
    <div className="relative flex items-start justify-between gap-3 sm:gap-4">
      {/* MAIN identity area */}
      <div className="min-w-0">
        <span className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-gradient-teal">A-ID</span>
        <p className="mt-0.5 text-[10px] font-mono uppercase tracking-[0.2em] text-teal">Your accessibility identity</p>

        <div className="mt-4 flex items-center gap-3.5 sm:gap-4">
          <span className="relative shrink-0">
            <span className="absolute -inset-1 rounded-full bg-teal/25 blur-md" aria-hidden="true" />
            <span className="relative block h-14 w-14 sm:h-[72px] sm:w-[72px] overflow-hidden rounded-full ring-2 ring-teal/70">
              <img
                src={AID_DEMO_PHOTO}
                alt="Portrait of Jordan Williams, a fictional A-ID demo user"
                className="h-full w-full scale-[1.4] object-cover"
                loading="lazy"
              />
            </span>
          </span>
          <div className="min-w-0">
            <p className="font-display text-lg sm:text-xl font-bold text-white leading-tight">Jordan Williams</p>
            <p className="mt-0.5 font-mono text-xs sm:text-sm text-teal">A-ID: AID-7X94-M2Q6</p>
            <p className="mt-1.5 text-xs sm:text-sm text-slate-300">
              <span className="text-slatemuted">ISSUED:</span> MAY 2025
            </p>
            <p className="text-xs sm:text-sm text-slate-300">
              <span className="text-slatemuted">EXPIRES:</span> MAY 2027
            </p>
          </div>
        </div>
      </div>

      {/* QR — top-right, fully contained inside the card */}
      <div className="flex shrink-0 flex-col items-center">
        <div className="rounded-xl bg-white p-2 shadow-[0_0_28px_-8px_rgba(0,240,255,0.7)] ring-1 ring-teal/40">
          <QRCodeSVG value={qrTarget} size={72} bgColor="#ffffff" fgColor="#0A1322" level="M" />
        </div>
        <p className="mt-1.5 text-center text-[9px] font-mono uppercase tracking-[0.16em] text-teal leading-tight">
          Scan to see an<br />example A-ID
        </p>
        <p className="mt-1 max-w-[112px] text-center text-[8px] leading-snug text-slatemuted">
          See how accessibility information can be securely shared.
        </p>
      </div>
    </div>

    {/* divider */}
    <div className="relative mt-5 h-px w-full bg-gradient-to-r from-transparent via-white/12 to-transparent" />

    {/* feature indicators */}
    <ul className="relative mt-4 grid grid-cols-3 gap-2 sm:gap-3">
      {FEATURES.map((f, i) => (
        <li key={i} className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-teal/40 bg-teal/5">
            <f.Icon className="h-4 w-4 text-teal" aria-hidden="true" />
          </span>
          <span className="text-[11px] leading-tight text-slate-200">
            {f.lines.map((l) => (
              <span key={l} className="block">{l}</span>
            ))}
          </span>
        </li>
      ))}
    </ul>
  </div>
  );
};
